import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Card } from "@shared/ui/card";
import {
  Search,
  User,
  Phone,
  MapPin,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import PreviewMessage from "../../../preview/preview";
import { useNotificationChoices } from "@features/ux/hooks";
import {
  useSearchConsumer,
  useSendDirectMessage,
  useTemplateList,
  useUserInfoSearch,
  useGetTemplateData,
} from "@features/ux/components/quickMessage/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/ui/collapsible";
import { useSignatureData } from "@features/ux/components/templates/hooks";
import { useGetChoices } from "@features/ux/components/shared/hooks";

const Badge = ({ children, className = "" }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>
    {children}
  </span>
);

const Label = ({ children, htmlFor = "", className = "" }) => (
  <label
    htmlFor={htmlFor}
    className={`block text-sm font-medium text-gray-700 mb-1 ${className}`}
  >
    {children}
  </label>
);

const CreateIndividualMessage = () => {
  const [formData, setFormData] = useState({
    channel: "",
    messageType: "0",
    emailFormat: "0",
    recipientType: "",
    searchRecipient: "",
    subject: "",
    message: "",
    emailBody: "",
    richTextBody: "",
    htmlTextBody: "",
    template: "",
    footer: "",
    signature: "",
    scheduleForLater: false,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const { data: signatureData } = useSignatureData();
  const { data: channelData } = useGetChoices(
    "channel_choice",
    "quick_messaging",
  );
  const { data: recipientTypeData } = useGetChoices(
    "audience_type",
    "quick_messaging",
  );
  const { data: templateTypeData } = useGetChoices("template_type");
  const { mutate: sendDirectMessage, isPending } = useSendDirectMessage();
  const { data: templateData } = useTemplateList(formData.recipientType);
  const remoteUtilityId = Number(useRemoteUtilityId());

  // Get template details
  const { data: templateDetailData } = useGetTemplateData(formData.template);

  // Consumer search hook
  const { data: consumerSearchResults } = useSearchConsumer(
    searchTerm,
    remoteUtilityId,
  );

  // User info search hook (for Business User and Technician)
  const { data: userInfoSearchResults } = useUserInfoSearch(
    searchTerm,
    remoteUtilityId,
  );

  // Determine which results to use based on recipient type
  const filteredRecipients =
    formData.recipientType === "1"
      ? consumerSearchResults?.result || []
      : userInfoSearchResults?.result || [];

  const channelsOptions =
    channelData?.result?.map((channel) => ({
      value: channel.key,
      label: channel.value,
    })) || [];

  const recipientTypeOptions =
    recipientTypeData?.result?.map((recipientType) => ({
      value: recipientType.key,
      label: recipientType.value,
    })) || [];

  const templateTypeOptions =
    templateTypeData?.result?.map((templateType) => ({
      value: templateType.key,
      label: templateType.value,
    })) || [];

  const signatureOptions =
    signatureData?.results?.map((signature) => ({
      value: signature.id.toString(),
      label: signature.name,
      isDefault: signature.isDefault,
    })) || [];
  const templateOptions =
    templateData?.results
      ?.filter((template) => template.statusDisplay === "Active")
      .map((template) => ({
        value: template.id,
        label: template.templateName,
      })) || [];
  console.log("templateOptions", templateData);
  const defaultSignatureId =
    signatureData?.results
      ?.find((signature) => signature.isDefault)
      ?.id?.toString() || "";

  const placeholderData = [
    {
      group: "CUSTOMER",
      items: [
        { label: "First Name", value: "first_name" },
        { label: "Last Name", value: "last_name" },
        { label: "Full Name", value: "full_name" },
        { label: "Account Number", value: "account_number" },
        { label: "Email", value: "email" },
        { label: "Phone Number", value: "phone_number" },
        { label: "Category", value: "category" },
        { label: "Sub Category", value: "sub_category" },
        { label: "Billing Address", value: "billing_address" },
        { label: "Service Address", value: "service_address" },
        { label: "Plan Name", value: "plan_name" },
      ],
    },

    {
      group: "METER",
      items: [
        { label: "Meter Number", value: "meter_number" },
        { label: "Meter Type", value: "meter_type" },
        { label: "Device Number", value: "device_number" },
        { label: "Installation Date", value: "installation_date" },
        { label: "Previous Reading", value: "previous_reading" },
        { label: "Previous Reading Date", value: "previous_reading_date" },
        { label: "Current Reading", value: "current_reading" },
        { label: "Current Reading Date", value: "current_reading_date" },
        { label: "Consumption Units", value: "consumption_units" },
      ],
    },

    {
      group: "BILLING",
      items: [
        { label: "Invoice Number", value: "invoice_number" },
        { label: "Billing Cycle", value: "billing_cycle" },
        {
          label: "Billing Period Start Date",
          value: "billing_period_start_date",
        },
        { label: "Billing Period End Date", value: "billing_period_end_date" },
        { label: "Bill Amount", value: "bill_amount" },
        { label: "Due Date", value: "due_date" },
        { label: "Late Fee Amount", value: "late_fee_amount" },
        { label: "Disconnection Date", value: "disconnection_date" },
      ],
    },

    {
      group: "PAYMENT",
      items: [
        { label: "Payment ID", value: "payment_id" },
        { label: "Payment Date", value: "payment_date" },
        { label: "Payment Channel", value: "payment_channel" },
        { label: "Payment Mode", value: "payment_mode" },
        { label: "Payment Amount", value: "payment_amount" },
        { label: "Remaining Balance", value: "remaining_balance" },
        { label: "Receipt Number", value: "receipt_number" },
      ],
    },

    {
      group: "SERVICE ORDER / WORK ORDER",
      items: [
        { label: "Service Order ID", value: "service_order_id" },
        { label: "Service Type", value: "service_type" },
        { label: "Requested Date", value: "requested_date" },
        { label: "Requested Time", value: "requested_time" },
        { label: "Technician Name", value: "technician_name" },
        { label: "Status", value: "status" },
      ],
    },

    {
      group: "SELF SERVICE SUPPORT",
      items: [
        { label: "Service Center Name", value: "service_center_name" },
        { label: "Service Center Email", value: "service_center_email" },
        { label: "Service Center Number", value: "service_center_number" },
        { label: "Service Center Location", value: "service_center_location" },
      ],
    },
  ];

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "inactive":
      case "overdue":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleRecipientSelect = (recipient) => {
    setSelectedRecipient(recipient);
    setSearchTerm("");
  };

  const showWhenCondition = (fieldName: string) => {
    return (formValues: any) => {
      const messageType = formValues.messageType;
      const channel = formValues.channel;
      const emailFormat = formValues.emailFormat;
      const scheduleForLater = formValues.scheduleForLater;

      switch (fieldName) {
        case "template":
          return messageType === "1";

        case "emailFormat":
          return channel === "0" && messageType === "0";
        case "subject":
          return channel === "0";

        case "info":
          return channel === "0" && messageType === "0";

        case "emailBody":
          return (
            (messageType === "1" && channel === "0") ||
            (channel === "0" && messageType === "0" && emailFormat === "0")
          );

        case "richTextBody":
          return channel === "0" && messageType === "0" && emailFormat === "1";

        case "htmlTextBody":
          return channel === "0" && messageType === "0" && emailFormat === "2";

        case "footer":
        case "signature":
          return channel === "0" && messageType === "0";

        case "message":
          return (
            (messageType === "1" && channel === "1") ||
            (channel === "1" && messageType === "0")
          );

        case "scheduleDate":
          return scheduleForLater === true;

        default:
          return false;
      }
    };
  };

  const formFields: FormField[] = [
    {
      name: "channel",
      type: "select",
      label: "Channel",
      options: channelsOptions,
      defaultValue: "",
      required: true,
      helperText: "Select the communication channel for sending your message",
      className: "col-span-2",
    },
    {
      name: "messageType",
      type: "radio",
      label: "Message Type",
      options: [
        { label: "Custom Message", value: "0" },
        { label: "Use Template", value: "1" },
      ],
      defaultValue: "0",
      required: false,
      helperText:
        "Choose whether to compose a custom message or use a pre-defined template",
      className: "col-span-2",
    },

    {
      name: "recipientType",
      type: "select",
      label: "Recipient Type",
      options: recipientTypeOptions,
      required: true,
      helperText: "Select the type of recipient you want to search for",
      className: "col-span-2",
    },
    {
      name: "searchRecipient",
      type: "custom",
      label: "Search Recipient",
      helperText:
        "Search for recipients by name or account number. Select a recipient to auto-fill their contact details.",
      className: "col-span-2",
      customRender: (field, form, error) => {
        if (selectedRecipient) {
          return (
            <Card className="p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">
                      {selectedRecipient.user?.name ||
                        `${selectedRecipient.firstName || ""} ${selectedRecipient.lastName || ""}`}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedRecipient.consumerNo ||
                        selectedRecipient.user?.email}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedRecipient.contactNumber}
                    </p>
                  </div>
                  <Badge
                    className={getStatusColor(
                      selectedRecipient.statusDisplay ||
                        selectedRecipient.status,
                    )}
                  >
                    {selectedRecipient.statusDisplay ||
                      selectedRecipient.status}
                  </Badge>
                </div>
                <button
                  onClick={() => setSelectedRecipient(null)}
                  className="text-sm text-blue-600 hover:text-blue-700 underline"
                >
                  Change
                </button>
              </div>
            </Card>
          );
        }

        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Enter name, account number, or phone number..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full h-10 pl-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                />
              </div>
              {searchTerm && searchTerm.length > 0 && searchTerm.length < 3 && (
                <p className="text-sm text-gray-500">
                  Enter at least 3 characters to search
                </p>
              )}
            </div>

            {filteredRecipients.length > 0 && (
              <div className="space-y-2">
                <Label>
                  Search Results ({filteredRecipients.length} found)
                </Label>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredRecipients.map((recipient) => (
                    <Card
                      key={recipient.id}
                      className="cursor-pointer hover:bg-gray-50 transition-colors p-4"
                      onClick={() => handleRecipientSelect(recipient)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-medium">
                                {recipient.user?.name ||
                                  `${recipient.firstName} ${recipient.lastName}`}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {recipient.consumerNo ||
                                  recipient.user?.username}
                              </p>
                            </div>
                            <Badge
                              className={getStatusColor(
                                recipient.statusDisplay || recipient.status,
                              )}
                            >
                              {recipient.statusDisplay || recipient.status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-500">
                            {recipient.territoryData?.service && (
                              <div className="flex items-center gap-2">
                                <MapPin className="h-3 w-3" />
                                <span>
                                  {recipient.territoryData.service.area},{" "}
                                  {recipient.territoryData.service.subArea},{" "}
                                  {recipient.territoryData.service.premise}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3" />
                              <span>
                                {recipient.contactNumber ||
                                  recipient.user?.email}
                              </span>
                            </div>
                          </div>
                          {recipient.outstandingBalance && (
                            <div className="text-sm">
                              <span className="text-gray-500">Balance: </span>
                              <span
                                className={`font-medium ${
                                  recipient.outstandingBalance === "$0.00"
                                    ? "text-green-600"
                                    : "text-gray-900"
                                }`}
                              >
                                {recipient.outstandingBalance}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {searchTerm &&
              filteredRecipients.length === 0 &&
              searchTerm.length >= 3 && (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">
                    No recipients found matching your search
                  </p>
                </div>
              )}
          </div>
        );
      },
    },
    {
      name: "template",
      type: "select",
      label: "Select Template",
      options: templateOptions,
      required: false,
      showWhen: showWhenCondition("template"),
      helperText:
        "Select a template that matches your selected channel. The subject and message will be auto-filled.",
      className: "col-span-2",
    },
    {
      name: "emailFormat",
      type: "radio",
      label: "Email Format",
      groupColorNeeded: false,
      showWhen: showWhenCondition("emailFormat"),
      group: "Message Content",
      options: [
        { label: "Plain Text", value: "0" },
        { label: "Rich Text", value: "1" },
        { label: "HTML", value: "2" },
      ],
      defaultValue: "0",
      required: false,
      helperText: "Choose between plain text, rich text editor, or raw HTML",
      className: "col-span-2 mt-2",
    },
    {
      name: "subject",
      type: "text-with-placeholder",
      label: "",
      placeholder: "Enter email subject (use {placeholders})",
      placeholders: placeholderData,
      showWhen: showWhenCondition("subject"),
      disabled: formData.messageType === "1",
      required: false,
      groupColorNeeded: false,
      group: "Message Content",
      helperText:
        "Write a clear subject line. Use placeholders like {customer_name} for personalization",
      className: "col-span-2",
    },
    {
      name: "info",
      type: "custom",
      label: "",
      required: false,
      className: "col-span-2 mt-2",
      groupColorNeeded: false,
      group: "Message Content",
      showWhen: showWhenCondition("info"),
      customRender: () => {
        const [isOpen, setIsOpen] = useState(false);
        return (
          <Collapsible
            open={isOpen}
            onOpenChange={setIsOpen}
            className="overflow-hidden border border-blue-300 rounded-lg bg-blue-50 px-6 py-4"
          >
            <CollapsibleTrigger asChild>
              <div className="flex justify-between items-center cursor-pointer -mx-6 px-6 py-2 rounded">
                <div className="flex items-center gap-3">
                  <Info
                    size={18}
                    className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0"
                  />
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    Using Placeholders for Dynamic Content
                  </span>
                </div>
                {isOpen ? (
                  <ChevronUp size={18} className="text-blue-600" />
                ) : (
                  <ChevronDown size={18} className="text-blue-600" />
                )}
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <div className="pt-3">
                <div className="text-xs text-blue-800 dark:text-blue-200 space-y-3">
                  <p>
                    Placeholders allow you to personalize messages with dynamic
                    data. Use the format{" "}
                    <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                      {"{{placeholder_name}}"}
                    </code>{" "}
                    in your subject and message body.
                  </p>

                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Click "Insert Placeholder" to browse available
                        placeholders by category
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Placeholders will be highlighted in your message for
                        easy identification
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Common examples:{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{customer_name}}"}
                        </code>
                        ,{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{bill_amount}}"}
                        </code>
                        ,{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{due_date}}"}
                        </code>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      },
    },
    {
      name: "emailBody",
      type: "textarea-with-placeholder",
      label: "",
      placeholder: "Enter email body. Use placeholders for dynamic content",
      placeholders: placeholderData,
      required: false,
      rows: 8,
      showWhen: showWhenCondition("emailBody"),
      disabled: formData.messageType === "1",
      groupColorNeeded: false,
      group: "Message Content",
      helperText:
        "Write your message content. Click 'Insert Placeholder' to add dynamic fields",
      className: "col-span-2",
    },
    {
      name: "richTextBody",
      type: "rich-text",
      label: "Email Body (Rich Text)",
      placeholder: "Compose your email...",
      placeholderNeeded: true,
      placeholders: placeholderData,
      showWhen: showWhenCondition("richTextBody"),
      groupColorNeeded: false,
      group: "Message Content",
      helperText:
        "Use the toolbar to format your email. Placeholders can be inserted using the buttons below",
      required: false,
      className: "col-span-2",
    },
    {
      name: "htmlTextBody",
      type: "html-text",
      label: "Email Body (HTML Text)",
      placeholder: "Compose your email...",
      placeholderNeeded: true,
      placeholders: placeholderData,
      showWhen: showWhenCondition("htmlTextBody"),
      groupColorNeeded: false,
      group: "Message Content",
      helperText: "Write raw HTML code. Use placeholders for dynamic content",
      required: false,
      className: "col-span-2",
    },
    {
      name: "message",
      type: "textarea-with-placeholder",
      label: "",
      placeholder: "Enter your message (use {placeholders})",
      placeholders: placeholderData,
      showWhen: showWhenCondition("message"),
      required: false,
      rows: 8,
      groupColorNeeded: false,
      group: "Message Content",
      helperText:
        "Compose your message. Use placeholders like {customer_name} for personalization",
      className: "col-span-2",
    },
    {
      name: "footer",
      type: "textarea",
      label: "Footer",
      groupColorNeeded: false,
      group: "Message Content",
      showWhen: showWhenCondition("footer"),
      required: false,
      helperText: "This content will appear at the bottom of your email",
      className: "col-span-2",
    },
    {
      name: "signature",
      type: "select",
      label: "Signature",
      options: signatureOptions,
      defaultValue: defaultSignatureId,
      groupColorNeeded: false,
      group: "Message Content",
      showWhen: showWhenCondition("signature"),
      required: false,
      helperText: "Select a signature to append to your email",
      className: "col-span-2",
    },
    {
      name: "scheduleForLater",
      type: "checkbox",
      label: "Schedule for Later",
      className: "col-span-2 block text-sm font-medium text-gray-700",
      required: false,
    },
    {
      name: "scheduleDate",
      type: "datepicker",
      label: "Schedule Date",
      showWhen: showWhenCondition("scheduleDate"),
      className: "col-span-2",
      required: false,
      helperText: "Schedule this message for later delivery",
      allowPast: false,
    },
    {
      name: "scheduleTime",
      type: "time",
      label: "Schedule Time",
      showWhen: showWhenCondition("scheduleDate"),
      className: "col-span-2",
      required: false,
      helperText: "Schedule this message for later delivery",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });
  useEffect(() => {
    if (templateDetailData && formData.messageType === "1") {
      const templateContent = templateDetailData.content;

      // Auto-populate subject and message from template
      setFormData((prev) => ({
        ...prev,
        subject: templateContent.subject || "",
        message: templateContent.body || "",
        emailBody: templateContent.body || "",
        footer: templateContent.footer || "",
      }));

      // Update form values
      form.setValue("subject", templateContent.subject || "");
      form.setValue("footer", templateContent.footer || "");

      if (formData.channel === "1") {
        // SMS channel
        form.setValue("message", templateContent.body || "");
      } else {
        // Email channel
        form.setValue("emailBody", templateContent.body || "");
      }
    }
  }, [templateDetailData, formData.messageType, formData.channel, form]);
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear search and selected recipient when recipient type changes
    if (field === "recipientType") {
      setSearchTerm("");
      setSelectedRecipient(null);
    }
  };

  useEffect(() => {
    console.log("Form state:", {
      errors: form.formState.errors,
      isValid: form.formState.isValid,
      isDirty: form.formState.isDirty,
      touchedFields: form.formState.touchedFields,
    });
  }, [form.formState]);

  const handleSubmit = async (data: any) => {
    console.log("🔥 HANDLE SUBMIT CALLED 🔥");
    console.log("Data:", data);
    const values = form.getValues();
    console.log("Submitting message:", values);
    console.log("Selected Recipient:", selectedRecipient);
    console.log("=== SUBMIT TRIGGERED ===");

    console.log("Form errors:", form.formState.errors);
    console.log("Is form valid:", form.formState.isValid);
    // Log based on selected format
    if (values.emailFormat === "1") {
      console.log("Rich text body:", values.richTextBody);
    } else if (values.emailFormat === "2") {
      console.log("HTML body:", values.htmlTextBody);
    }

    // Determine the body content based on channel and format
    let bodyContent = "";
    if (values.channel === "EMAIL") {
      if (values.emailFormat === "0") {
        bodyContent = values.emailBody;
      } else if (values.emailFormat === "1") {
        bodyContent = values.richTextBody;
      } else if (values.emailFormat === "2") {
        bodyContent = values.htmlTextBody;
      }
    } else if (values.channel === "SMS") {
      bodyContent = values.message;
    }

    const payload = {
      remote_utility_id: getRemoteUtilityId(),
      channel_type: values.channel,
      to:
        values.channel === "1" // SMS channel
          ? selectedRecipient?.contactNumber
            ? [selectedRecipient.contactNumber]
            : selectedRecipient?.user?.phone
              ? [selectedRecipient.user.phone]
              : []
          : selectedRecipient?.email
            ? [selectedRecipient.email]
            : selectedRecipient?.user?.email
              ? [selectedRecipient.user.email]
              : [],
      template_id: values.template ? values.template : null,
      schedule_date: values.scheduleForLater ? values.scheduleDate : null,
      schedule_time: values.scheduleForLater ? values.scheduleTime : null,
      subject: values.subject,
      body:
        values.channel === "0"
          ? values.emailFormat === "0"
            ? values.emailBody
            : values.emailFormat === "1"
              ? values.richTextBody
              : values.htmlTextBody
          : values.message,
      signature: values.signature
        ? parseInt(values.signature)
        : defaultSignatureId
          ? parseInt(defaultSignatureId)
          : null,
      footer: values.footer,
    };

    console.log("API Payload:", payload);

    sendDirectMessage(payload, {
      onSuccess: (data) => {
        form.reset();
        setSelectedRecipient(null);
      },
    });
  };

  const handleCancel = () => {
    form.reset();
    setSelectedRecipient(null);
    setSearchTerm("");
  };

  return (
    <div>
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-6">
          <DynamicForm
            fields={formFields}
            form={form}
            gridColumns={2}
            responsiveColumns={{ sm: 1, md: 2 }}
            onSubmit={handleSubmit}
            onReset={handleCancel}
            loading={isPending}
            onFieldChange={handleFieldChange}
            config={{
              resetButtonText: "Cancel",
              submitButtonText: formData.scheduleForLater
                ? "Schedule Message"
                : "Send Message",
              layout: "grid",
              columns: 2,
              showSubmitButton: true,
              showResetButton: false,
              spacing: "medium",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700",
                buttonContainer: "flex justify-end gap-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                select:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            }}
          />
        </Card>
        <div className="col-span-6">
          <PreviewMessage
            channel={formData.channel}
            subject={formData.subject}
            message={formData.message}
            emailBody={formData.emailBody}
            richTextBody={formData.richTextBody}
            htmlTextBody={formData.htmlTextBody}
            footer={formData.footer}
            signature={formData.signature}
            emailFormat={formData.emailFormat}
            channelOptions={channelsOptions}
            signatureOptions={signatureOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateIndividualMessage;
